import React from "react";
import { PrimaryBlueButton, SecondaryBlueButton } from "@velaro/velaro-component-lib";
import FormGroup from "@/components/FormGroup";

export type DisplayConditionType =
  | "url_contains" | "url_equals" | "url_matches" | "always"
  | "time_of_day" | "day_of_week" | "return_visitor";

export interface DisplayCondition {
  type: DisplayConditionType;
  value: string;
}

export interface DisplayRule {
  id: string;
  operator: "any" | "all";
  conditions: DisplayCondition[];
  showAfterSeconds: number;
  showAfterScrollPercent: number;
}

function newRule(): DisplayRule {
  return {
    id: Math.random().toString(36).slice(2),
    operator: "any",
    conditions: [{ type: "url_contains", value: "" }],
    showAfterSeconds: 0,
    showAfterScrollPercent: 0
  };
}

const DAY_OPTIONS = [
  { value: "0", label: "Sun" },
  { value: "1", label: "Mon" },
  { value: "2", label: "Tue" },
  { value: "3", label: "Wed" },
  { value: "4", label: "Thu" },
  { value: "5", label: "Fri" },
  { value: "6", label: "Sat" },
];

interface Props {
  rules: DisplayRule[];
  onChange(rules: DisplayRule[]): void;
}

const CONDITION_TYPE_LABELS: Record<DisplayConditionType, string> = {
  url_contains: "URL contains",
  url_equals: "URL equals",
  url_matches: "URL matches pattern",
  always: "Always show",
  time_of_day: "Time of day",
  day_of_week: "Day of week",
  return_visitor: "Visitor type",
};

export default function DisplayRulesEditor({ rules, onChange }: Props) {
  function addRule() {
    onChange([...rules, newRule()]);
  }

  function removeRule(id: string) {
    onChange(rules.filter((r) => r.id !== id));
  }

  function updateRule(id: string, updates: Partial<DisplayRule>) {
    onChange(rules.map((r) => (r.id === id ? { ...r, ...updates } : r)));
  }

  function addCondition(ruleId: string) {
    onChange(
      rules.map((r) =>
        r.id === ruleId
          ? { ...r, conditions: [...r.conditions, { type: "url_contains", value: "" }] }
          : r
      )
    );
  }

  function removeCondition(ruleId: string, index: number) {
    onChange(
      rules.map((r) =>
        r.id === ruleId
          ? { ...r, conditions: r.conditions.filter((_, i) => i !== index) }
          : r
      )
    );
  }

  function updateCondition(ruleId: string, index: number, updates: Partial<DisplayCondition>) {
    onChange(
      rules.map((r) =>
        r.id === ruleId
          ? {
              ...r,
              conditions: r.conditions.map((c, i) => (i === index ? { ...c, ...updates } : c))
            }
          : r
      )
    );
  }

  if (rules.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-8 border-2 border-dashed border-gray-200 rounded-lg text-center">
        <div className="text-gray-400 text-sm">No display rules — widget shows on all pages</div>
        <PrimaryBlueButton label="Add Display Rule" onClick={addRule} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {rules.map((rule, ruleIndex) => (
        <div key={rule.id} className="border border-gray-200 rounded-lg p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="font-medium text-sm text-gray-700">
              Rule {ruleIndex + 1}
            </div>
            <button
              onClick={() => removeRule(rule.id)}
              className="text-red-400 hover:text-red-600 text-xs"
              aria-label="Remove rule"
            >
              Remove rule
            </button>
          </div>

          {/* Conditions */}
          <div className="flex flex-col gap-2">
            {rule.conditions.map((condition, condIndex) => (
              <div key={condIndex} className="flex items-center gap-2">
                {condIndex > 0 && (
                  <span className="text-xs text-gray-400 w-6 text-center">OR</span>
                )}
                {condIndex === 0 && <span className="w-6" />}

                <select
                  className="input text-sm flex-shrink-0"
                  value={condition.type}
                  onChange={(e) =>
                    updateCondition(rule.id, condIndex, { type: e.target.value as DisplayCondition["type"] })
                  }
                >
                  {Object.entries(CONDITION_TYPE_LABELS).map(([val, label]) => (
                    <option key={val} value={val}>{label}</option>
                  ))}
                </select>

                {condition.type === "time_of_day" && (
                  <div className="flex items-center gap-1 flex-1">
                    <input
                      type="time"
                      className="input text-sm"
                      value={condition.value.split("-")[0] || "09:00"}
                      onChange={(e) => {
                        const end = condition.value.split("-")[1] || "17:00";
                        updateCondition(rule.id, condIndex, { value: `${e.target.value}-${end}` });
                      }}
                    />
                    <span className="text-xs text-gray-400">to</span>
                    <input
                      type="time"
                      className="input text-sm"
                      value={condition.value.split("-")[1] || "17:00"}
                      onChange={(e) => {
                        const start = condition.value.split("-")[0] || "09:00";
                        updateCondition(rule.id, condIndex, { value: `${start}-${e.target.value}` });
                      }}
                    />
                  </div>
                )}
                {condition.type === "day_of_week" && (
                  <div className="flex gap-1 flex-wrap">
                    {DAY_OPTIONS.map((day) => {
                      const selected = (condition.value || "").split(",").includes(day.value);
                      return (
                        <button
                          key={day.value}
                          type="button"
                          onClick={() => {
                            const days = (condition.value || "").split(",").filter(Boolean);
                            const next = selected
                              ? days.filter((d) => d !== day.value)
                              : [...days, day.value].sort();
                            updateCondition(rule.id, condIndex, { value: next.join(",") });
                          }}
                          className={`px-2 py-1 rounded text-xs font-medium border transition-colors ${
                            selected
                              ? "bg-primary-bg border-primary-hover text-primary-active"
                              : "bg-white border-gray-200 text-gray-500 hover:border-gray-400"
                          }`}
                        >
                          {day.label}
                        </button>
                      );
                    })}
                  </div>
                )}
                {condition.type === "return_visitor" && (
                  <select
                    className="input text-sm"
                    value={condition.value || "true"}
                    onChange={(e) => updateCondition(rule.id, condIndex, { value: e.target.value })}
                  >
                    <option value="true">Return visitors only</option>
                    <option value="false">First-time visitors only</option>
                  </select>
                )}
                {condition.type !== "always" && condition.type !== "time_of_day" && condition.type !== "day_of_week" && condition.type !== "return_visitor" && (
                  <input
                    className="input text-sm flex-1"
                    value={condition.value}
                    onChange={(e) => updateCondition(rule.id, condIndex, { value: e.target.value })}
                    placeholder={
                      condition.type === "url_contains" ? "/products"
                      : condition.type === "url_equals" ? "https://example.com/pricing"
                      : "/blog/*"
                    }
                  />
                )}

                {rule.conditions.length > 1 && (
                  <button
                    onClick={() => removeCondition(rule.id, condIndex)}
                    className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                    aria-label="Remove condition"
                  >
                    <i className="fa-solid fa-xmark" />
                  </button>
                )}
              </div>
            ))}
          </div>

          <button
            onClick={() => addCondition(rule.id)}
            className="text-primary text-xs hover:underline text-left"
          >
            + Add another condition
          </button>

          {/* Timing */}
          <div className="flex gap-4 pt-2 border-t border-gray-100">
            <FormGroup label="Show after (seconds)" hint="0 = immediate">
              <input
                type="number"
                className="input w-24 text-sm"
                min={0}
                max={300}
                value={rule.showAfterSeconds}
                onChange={(e) =>
                  updateRule(rule.id, { showAfterSeconds: Math.max(0, Number(e.target.value)) })
                }
              />
            </FormGroup>
            <FormGroup label="Show after scroll %" hint="0 = any scroll">
              <input
                type="number"
                className="input w-24 text-sm"
                min={0}
                max={100}
                value={rule.showAfterScrollPercent}
                onChange={(e) =>
                  updateRule(rule.id, { showAfterScrollPercent: Math.max(0, Math.min(100, Number(e.target.value))) })
                }
              />
            </FormGroup>
          </div>
        </div>
      ))}

      <PrimaryBlueButton label="+ Add Another Rule" onClick={addRule} />
    </div>
  );
}
